import type { CmmdProjectUpdateStatus } from "@cmmd-forge/contracts";

import { MarkdownEditor } from "~/components/rich-content/MarkdownEditor";
import { ProjectContentReadabilityControls } from "~/components/rich-content/ProjectContentReadabilityControls";
import { ProjectUpdateStatusPicker } from "./ProjectUpdateStatusPicker";

export interface ProjectUpdateComposerFieldsProps {
  readonly projectId: string;
  readonly status: CmmdProjectUpdateStatus;
  readonly body: string;
  readonly saving: boolean;
  readonly error: string | null;
  readonly ariaLabel: string;
  readonly submitLabel: string;
  readonly submittingLabel: string;
  readonly onStatusChange: (status: CmmdProjectUpdateStatus) => void;
  readonly onBodyChange: (body: string) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
}

/**
 * Shared writing surface for posting and editing project status updates.
 * Compact chrome keeps status → body → actions as one composition.
 */
export function ProjectUpdateComposerFields(props: ProjectUpdateComposerFieldsProps) {
  return (
    <div
      className="rounded-xl bg-muted/25 p-3.5"
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && !props.saving) {
          event.preventDefault();
          event.stopPropagation();
          props.onCancel();
        }
      }}
    >
      <ProjectUpdateStatusPicker
        value={props.status}
        disabled={props.saving}
        onChange={props.onStatusChange}
      />
      <MarkdownEditor
        density="compact"
        value={props.body}
        onChange={props.onBodyChange}
        autoFocus
        ariaLabel={props.ariaLabel}
        placeholder="What's the latest on this project?"
        maxLength={10_000}
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <ProjectContentReadabilityControls
          layout="inline"
          projectId={props.projectId}
          surface="status_update"
          value={props.body}
          disabled={props.saving}
          onChange={props.onBodyChange}
        />
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={props.onSubmit}
            disabled={props.saving || !props.body.trim() || props.body.length > 10_000}
            className="inline-flex h-7 items-center rounded-md bg-foreground px-2.5 text-xs font-medium text-background transition-opacity duration-150 hover:opacity-90 disabled:opacity-50"
          >
            {props.saving ? props.submittingLabel : props.submitLabel}
          </button>
          <button
            type="button"
            onClick={props.onCancel}
            disabled={props.saving}
            className="inline-flex h-7 items-center rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>
      {props.error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}
